import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2, Shield, Clock } from "lucide-react";
import AdminShell, { Panel, StatusPill, Btn } from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import KpiCard from "@/components/admin/KpiCard";
import { api } from "@/lib/api";
import { useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const STATUS_TABS = [
  { k: "pending_admin", label: "Pending Approval" },
  { k: "cooling", label: "Cooling" },
  { k: "active", label: "Active" },
  { k: "rejected", label: "Rejected" },
];

export default function DepositWhitelistApprovals() {
  const [tab, setTab] = useState("pending_admin");
  const [rows, setRows] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const pager = useAdminPager();

  const load = async () => {
    const [a, k] = await Promise.all([
      api.get("/api/admin/wallet/deposit-whitelist", { params: { status: tab, skip: pager.skip, limit: pager.limit } }),
      api.get("/api/admin/wallet/kpis"),
    ]);
    const { items, total } = unwrapAdminList(a.data, "deposit_whitelist");
    setRows(items);
    pager.setTotal(total);
    setKpis(k.data);
  };

  useEffect(() => { pager.resetPage(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, pager.page]);

  const approve = async (wid) => {
    setBusy(wid);
    setError("");
    try {
      await api.post(`/api/admin/wallet/deposit-whitelist/${wid}/approve`, {
        note: "Approved by ops",
      });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  const reject = async (wid) => {
    const reason = window.prompt("Rejection reason:");
    if (!reason) return;
    setBusy(wid);
    setError("");
    try {
      await api.post(`/api/admin/wallet/deposit-whitelist/${wid}/reject`, { reason });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <AdminShell title="Deposit whitelist" subtitle="Review source addresses users want to deposit from.">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard
          label="Pending Deposit Addresses"
          value={kpis?.pending_deposit_whitelist_addresses ?? "—"}
          icon={Clock}
          variant="yellow"
        />
        <KpiCard
          label="Pending Withdrawal Addresses"
          value={kpis?.pending_whitelist_addresses ?? "—"}
          icon={Shield}
          variant="purple"
        />
      </div>

      <Panel className="mb-5">
        <div className="flex flex-wrap gap-2 p-3">
          {STATUS_TABS.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-3 py-1.5 rounded-md text-[12px] font-semibold ${
                tab === t.k
                  ? "bg-primary/20 text-primary"
                  : "bg-surface-2 text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </Panel>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-500/10 text-red-300 text-[13px]">{error}</div>
      )}

      <Panel>
        {!rows ? (
          <div className="p-8 text-center text-muted-foreground">
            <Loader2 className="animate-spin mx-auto mb-2" size={24} />
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-[13px]">No entries in this queue.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-line">
                  <th className="p-3">User</th>
                  <th className="p-3">Asset</th>
                  <th className="p-3">Address</th>
                  <th className="p-3">Own?</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Submitted</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-line/60 hover:bg-surface-2/50">
                    <td className="p-3 font-mono text-[11px]">{r.user_email || r.user_id}</td>
                    <td className="p-3">
                      {r.asset} / {r.network}
                    </td>
                    <td className="p-3 font-mono text-[11px] max-w-[200px] truncate" title={r.address}>
                      {r.address}
                    </td>
                    <td className="p-3">{r.is_own_address ? "Yes" : "No"}</td>
                    <td className="p-3">
                      <StatusPill value={r.status_label || r.status} />
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="p-3 text-right">
                      {r.status === "pending_admin" && (
                        <div className="flex justify-end gap-2">
                          <Btn kind="success" disabled={busy === r.id} onClick={() => approve(r.id)}>
                            {busy === r.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <CheckCircle2 size={12} />
                            )}
                            Approve
                          </Btn>
                          <Btn kind="danger" disabled={busy === r.id} onClick={() => reject(r.id)}>
                            <XCircle size={12} /> Reject
                          </Btn>
                        </div>
                      )}
                      {r.status === "cooling" && r.cooling_until && !r.is_active && (
                        <span className="text-[11px] text-amber-400">
                          Cooling until {new Date(r.cooling_until).toLocaleString()}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          </div>
        )}
        <AdminPager
          page={pager.page}
          total={pager.total}
          pageSize={pager.pageSize}
          onPage={pager.setPage}
          testId="deposit-whitelist-pager"
        />
      </Panel>
    </AdminShell>
  );
}
